import { useState } from "react";
import { toast } from "sonner";
import { Boxes, PackageOpen, PiggyBank, Move, FileDown } from "lucide-react";
import { assets } from "@/data/fixtures";
import SourceChip from "@/components/SourceChip";
import ApprovalModal from "@/components/ApprovalModal";

// Recompute the memo total from line items to enforce F4-AC-02 at render time.
function computeTotal() {
  const sum = [
    ...assets.moveKits.map((m) => parseInt(m.bookValue.replace(/[^0-9]/g, ""), 10)),
    ...assets.recoverSets.map((r) => parseInt(r.bookValue.replace(/[^0-9]/g, ""), 10)),
    ...assets.deferPurchases.map((d) => parseInt(d.figure.replace(/[^0-9]/g, ""), 10)),
  ].reduce((a, b) => a + b, 0);
  return `$${sum.toLocaleString("en-US")}`;
}

export default function AssetMemo() {
  const [executeTarget, setExecuteTarget] = useState(null);
  const computedTotal = computeTotal();
  const totalMatches = computedTotal === assets.totalImpact;

  const onExportMemo = () =>
    toast.success("Monthly reallocation memo exported · PDF", { duration: 4000 });
  const onApproveExecute = () => {
    toast.success(
      `Reallocation logged · ${executeTarget.label} · pending kit-ops dispatch`,
      { duration: 4200 }
    );
    setExecuteTarget(null);
  };

  return (
    <div data-testid="page-asset-memo" className="space-y-8">
      {/* Header */}
      <div className="flex items-end justify-between gap-6">
        <div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500 mb-2">
            Monthly Asset Reallocation Memo
          </div>
          <h1 className="text-[30px] leading-tight font-semibold text-slate-900 tracking-tight">
            Total impact{" "}
            <span className="mos-mono" data-testid="memo-total">
              {computedTotal}
            </span>
          </h1>
          <p className="mt-2 text-[13.5px] text-slate-600">
            {assets.period}. Every dollar decomposes to book values and specific asset IDs (SP-02).
          </p>
          {!totalMatches && (
            <p className="mt-2 text-[12px] text-[color:var(--mos-red)]" data-testid="total-mismatch-warning">
              Total does not reconcile with line items — fixture drift detected.
            </p>
          )}
        </div>

        <button
          type="button"
          onClick={onExportMemo}
          className="mos-btn mos-btn-secondary"
          data-testid="btn-export-memo"
        >
          <FileDown size={14} />
          Export memo
        </button>
      </div>

      {/* Memo body · three sections */}
      <div className="space-y-8">
        {/* Move these kits */}
        <MemoSection
          icon={Move}
          title="Move these kits"
          subtitle="Idle at low-demand accounts · move to accounts with unmet demand"
          testId="section-move-kits"
        >
          {assets.moveKits.map((m) => (
            <MemoRow
              key={m.id}
              testId={`move-kit-${m.id}`}
              lead={
                <div>
                  <div className="text-[14.5px] font-medium text-slate-900">
                    {m.assetIds.join(", ")}
                  </div>
                  <div className="text-[12.5px] text-slate-500 mt-0.5">
                    From <span className="text-slate-700">{m.fromAccount}</span> → to{" "}
                    <span className="text-slate-700">{m.toAccount}</span>
                  </div>
                </div>
              }
              rationale={m.rationale}
              figure={m.bookValue}
              figureLabel="Book value"
              source={m.source}
              onExecute={() =>
                setExecuteTarget({
                  label: m.assetIds.join(" + "),
                  action: `Reallocate ${m.assetIds.join(", ")} · ${m.fromAccount} → ${m.toAccount}`,
                  systems: ["ERP · fixed assets", "Kit management"],
                })
              }
              actionLabel="Execute move"
            />
          ))}
        </MemoSection>

        {/* Recover these sets */}
        <MemoSection
          icon={PackageOpen}
          title="Recover these sets"
          subtitle="Past the configurable return window · recall for reprocessing"
          testId="section-recover-sets"
        >
          {assets.recoverSets.map((r) => (
            <MemoRow
              key={r.id}
              testId={`recover-set-${r.id}`}
              lead={
                <div>
                  <div className="text-[14.5px] font-medium text-slate-900">
                    {r.assetIds.join(", ")}
                  </div>
                  <div className="text-[12.5px] text-slate-500 mt-0.5">
                    At <span className="text-slate-700">{r.account}</span>
                  </div>
                </div>
              }
              rationale={
                <span>
                  Overdue by{" "}
                  <span className="mos-mono text-slate-900">{r.overdueDays}</span> days
                  against a{" "}
                  <span className="mos-mono text-slate-900">{r.returnWindowDays}</span>-day
                  return window.
                </span>
              }
              figure={r.bookValue}
              figureLabel="Book value recovered"
              source={r.source}
              onExecute={() =>
                setExecuteTarget({
                  label: r.assetIds.join(" + "),
                  action: `Recall ${r.assetIds.join(", ")} from ${r.account}`,
                  systems: ["Kit management", "Field ops queue"],
                })
              }
              actionLabel="Issue recall"
            />
          ))}
        </MemoSection>

        {/* Defer this capital */}
        <MemoSection
          icon={PiggyBank}
          title="Defer these capital purchases"
          subtitle="Coverage achievable via moves and recoveries above"
          testId="section-defer-capex"
        >
          {assets.deferPurchases.map((d) => (
            <MemoRow
              key={d.id}
              testId={`defer-capex-${d.id}`}
              lead={
                <div>
                  <div className="text-[14.5px] font-medium text-slate-900">
                    {d.description}
                  </div>
                </div>
              }
              rationale={d.rationale}
              figure={d.figure}
              figureLabel="Deferrable capital"
              source={d.source}
              onExecute={() =>
                setExecuteTarget({
                  label: d.description,
                  action: `Defer capital purchase · ${d.description}`,
                  systems: ["ERP · capital request"],
                })
              }
              actionLabel="Defer request"
            />
          ))}
        </MemoSection>
      </div>

      <p className="text-[11.5px] text-slate-500 flex items-center gap-2">
        <Boxes size={12} />
        Every action above is a recommendation. Execution requires a named approval and updates the field-asset registry.
      </p>

      <ApprovalModal
        open={!!executeTarget}
        onCancel={() => setExecuteTarget(null)}
        onApprove={onApproveExecute}
        title="Execute reallocation"
        rationale="Executing writes to the field-asset registry and ERP fixed-asset records. Dispatch and reprocessing steps remain manual."
        approver="M. Chen"
        action={executeTarget ? executeTarget.action : ""}
        systemsAffected={executeTarget ? executeTarget.systems : []}
        auditNote="Execution and downstream updates will each write to the audit log."
        confirmLabel="Approve and execute"
      />
    </div>
  );
}

function MemoSection({ icon: Icon, title, subtitle, children, testId }) {
  return (
    <section data-testid={testId}>
      <div className="flex items-center gap-3 mb-4">
        <span
          className="h-8 w-8 rounded-full inline-flex items-center justify-center flex-none"
          style={{ background: "#EFF3F9" }}
        >
          <Icon size={14} style={{ color: "var(--mos-accent)" }} />
        </span>
        <div>
          <div className="text-[15px] font-semibold text-slate-900">{title}</div>
          <div className="text-[12.5px] text-slate-500">{subtitle}</div>
        </div>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function MemoRow({ lead, rationale, figure, figureLabel, source, onExecute, actionLabel, testId }) {
  return (
    <div className="mos-surface rounded-md p-5" data-testid={testId}>
      <div className="flex items-start gap-6">
        <div className="flex-1 min-w-0">
          {lead}
          <p className="mt-2 text-[13px] text-slate-700 leading-relaxed">
            {rationale}
          </p>
          <div className="mt-3">
            <SourceChip system={source.system} record={source.record} />
          </div>
        </div>
        <div className="flex flex-col items-end gap-3 flex-none">
          <div className="text-right">
            <div className="text-[10.5px] uppercase tracking-wider text-slate-500">
              {figureLabel}
            </div>
            <div className="mos-mono text-[19px] font-semibold text-slate-900 mt-0.5">
              {figure}
            </div>
          </div>
          <button
            type="button"
            onClick={onExecute}
            className="mos-btn mos-btn-secondary"
          >
            {actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
